import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AuthContext } from '../context/AuthContext';
import { TbLayoutSidebarRightExpand } from 'react-icons/tb';
import MobileMenu from './MobileMenu';

const OuterContainer = styled.div`
  display: flex;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
`;

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  max-width: 1600px;
  padding: 0 40px;
  position: relative;
  justify-content: center;

  @media (max-width: 1220px) {
    flex-direction: column;
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  justify-content: flex-start;
  
  @media (max-width: 1220px) {
    flex-direction: column;
  }
`;

const SideNav = styled.nav`
  width: 180px;
  padding: 80px 0 40px;
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  margin-right: 40px;

  @media (max-width: 1220px) {
    display: none;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 80px 0 40px;
  min-height: 100vh;
  max-width: 960px;
  width: 100%;
  position: relative;

  @media (max-width: 1220px) {
    margin-left: 0;
    padding: 0;
    max-width: 100%;
    left: 0;
    transform: none;
    background: white;
    border-radius: 0;
    box-shadow: ${props => props.$menuOpen ? '0 0 20px rgba(0, 0, 0, 0.15)' : 'none'};
    transition: box-shadow 0.3s ease;
  }
`;

const Logo = styled(Link)`
  display: block;
  margin-bottom: 10px;
  
  img {
    width: 40px;
    height: auto;
  }
`;

const BrandName = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin-bottom: 30px;
  text-align: left;
`;

const NavLink = styled(Link)`
  color: rgba(46, 46, 46, 0.7);
  text-decoration: none;
  font-size: 16px;
  margin: 8px 0;
  transition: color 0.2s ease;
  text-transform: lowercase;

  &:hover, &.active {
    color: #2E2E2E;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(46, 46, 46, 0.1);
  margin: 20px 0;
  width: 100%;
`;

const MobileHeader = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: white;
  z-index: 997;
  padding: 0 20px;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1220px) {
    display: flex;
  }
`;

const MobileLogo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  
  img {
    width: 30px;
    height: auto;
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #2E2E2E;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -8px;
  
  &:hover {
    opacity: 0.7;
  }
`;

const MobileContentWrapper = styled.div`
  width: 100%;
  
  @media (max-width: 1220px) {
    margin-top: 60px;
    background: white;
    position: relative;
    z-index: ${props => props.$menuOpen ? '998' : '1'};
    transition: transform 0.3s ease;
    transform: ${props => props.$menuOpen ? 'translateX(-280px)' : 'none'};
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Layout = ({ children }) => {
  const location = useLocation();
  const { user } = React.useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <OuterContainer>
      <MobileHeader>
        <MobileLogo to="/">
          <img src="/images/logo/logo_iconmark.png" alt="Tempo Stock Logo" />
        </MobileLogo>
        <MenuButton onClick={() => setIsMobileMenuOpen(true)}>
          <TbLayoutSidebarRightExpand strokeWidth={1.5} />
        </MenuButton>
      </MobileHeader>
      
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
      
      <LayoutContainer>
        <ContentWrapper>
          <SideNav>
            <Logo to="/">
              <img src="/images/logo/logo_iconmark.png" alt="Tempo Stock Logo" />
            </Logo>
            <BrandName>tempo stock</BrandName>
            <NavLink to="/" className={location.pathname === '/' ? 'active' : ''}>
              search
            </NavLink>
            <NavLink to="/collections" className={location.pathname === '/collections' ? 'active' : ''}>
              collections
            </NavLink>
            <NavLink to="/about" className={location.pathname === '/about' ? 'active' : ''}>
              about
            </NavLink>
            <NavLink to="/blog" className={location.pathname === '/blog' ? 'active' : ''}>
              blog
            </NavLink>
            <Divider />
            <NavLink to="/pricing" className={location.pathname === '/pricing' ? 'active' : ''}>
              pricing
            </NavLink>
            {user ? (
              <NavLink to="/user" className={location.pathname === '/user' ? 'active' : ''}>
                my account
              </NavLink>
            ) : (
              <NavLink to="/login" className={location.pathname === '/login' ? 'active' : ''}>
                login
              </NavLink>
            )}
          </SideNav>
          <MobileContentWrapper $menuOpen={isMobileMenuOpen}>
            <MainContent $menuOpen={isMobileMenuOpen}>
              {children}
            </MainContent>
          </MobileContentWrapper>
        </ContentWrapper>
      </LayoutContainer>
    </OuterContainer>
  );
};

export default Layout; 